import { Cloud, ImageIcon, Loader2, MessageSquarePlus, Paperclip, Send, Sparkles, StopCircle, X } from "lucide-react";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { GoogleDriveBrowser } from "@/components/google-drive/GoogleDriveBrowser";
import { VoiceInputButton } from "@/components/shared/VoiceInputButton";
import { SharePointBrowser } from "@/components/sharepoint/SharePointBrowser";
import { getRuntimeConfig } from "@/config/runtime";
import { ALLOWED_IMAGE_TYPES } from "@/constants/files";
import { sharepointService } from "@/lib/services";
import type { SharePointItem, SpecialistAgent } from "@/types";
import { AgentAvatar } from "./AgentAvatar";

/** Regex that matches SharePoint / OneDrive document URLs. */
const SHAREPOINT_URL_RE = /https?:\/\/[a-zA-Z0-9-]+(?:-my)?\.sharepoint\.com\/[^\s)>"]+/g;

/** Detect an active @mention query from the text before the cursor. */
export function detectMention(text: string, cursorPos: number): { query: string; start: number } | null {
  if (cursorPos <= 0) return null;

  const textBeforeCursor = text.slice(0, cursorPos);
  const lastAtIndex = textBeforeCursor.lastIndexOf("@");
  if (lastAtIndex < 0) return null;

  // @ must be at start or preceded by whitespace
  const charBefore = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " ";
  if (charBefore !== " " && charBefore !== "\n" && lastAtIndex !== 0) return null;

  const query = textBeforeCursor.slice(lastAtIndex + 1);
  // Only show autocomplete if query doesn't contain spaces (still typing the name)
  if (query.includes(" ")) return null;

  return { query, start: lastAtIndex };
}

/** Handle keyboard events for the @mention dropdown. Returns true if the event was consumed. */
export function handleMentionKeyDown(
  e: KeyboardEvent<HTMLTextAreaElement>,
  filteredAgents: SpecialistAgent[],
  mentionIndex: number,
  setMentionIndex: (fn: (prev: number) => number) => void,
  insertMention: (agent: SpecialistAgent) => void,
  closeMention: () => void,
): boolean {
  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      setMentionIndex((prev) => Math.min(prev + 1, filteredAgents.length - 1));
      return true;
    case "ArrowUp":
      e.preventDefault();
      setMentionIndex((prev) => Math.max(prev - 1, 0));
      return true;
    case "Tab":
    case "Enter":
      e.preventDefault();
      insertMention(filteredAgents[mentionIndex]);
      return true;
    case "Escape":
      e.preventDefault();
      closeMention();
      return true;
    default:
      return false;
  }
}

export interface GroupChatSendPayload {
  message: string;
  imageFiles?: File[];
  documentFiles?: File[];
  sharepointItems?: SharePointItem[];
}

interface GroupChatInputProps {
  onSendMessage: (payload: GroupChatSendPayload) => void;
  onInterrupt?: () => void;
  onContinueDiscussion?: () => void;
  onSynthesize?: () => void;
  isLoading: boolean;
  isActive: boolean;
  disabled?: boolean;
  isDownloadingFiles?: boolean;
  pendingInput?: string;
  onPendingInputConsumed?: () => void;
  agents?: SpecialistAgent[];
}

export function GroupChatInput({
  // NOSONAR -- input component with mentions, file uploads, drag-drop, and send logic
  onSendMessage,
  onInterrupt,
  onContinueDiscussion,
  onSynthesize,
  isLoading,
  isActive,
  disabled = false,
  isDownloadingFiles = false,
  pendingInput,
  onPendingInputConsumed,
  agents = [],
}: Readonly<GroupChatInputProps>) {
  const [inputValue, setInputValue] = useState("");
  const [pendingImageFiles, setPendingImageFiles] = useState<File[]>([]);
  const [pendingDocumentFiles, setPendingDocumentFiles] = useState<File[]>([]);
  const [sharepointFiles, setSharepointFiles] = useState<SharePointItem[]>([]);
  const [isResolvingUrl, setIsResolvingUrl] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isSharePointBrowserOpen, setIsSharePointBrowserOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Accept pending input from quick actions
  useEffect(() => {
    if (pendingInput) {
      setInputValue(pendingInput);
      onPendingInputConsumed?.();
      // Focus the textarea so the user can edit or just hit Enter
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [pendingInput, onPendingInputConsumed]);

  // Filter agents based on mention query
  const filteredAgents =
    mentionQuery !== null ? agents.filter((a) => a.name.toLowerCase().startsWith(mentionQuery.toLowerCase())) : [];

  // Reset mention index when filtered list changes
  useEffect(() => {
    setMentionIndex(0);
  }, [mentionQuery]);

  const closeMention = useCallback(() => {
    setMentionQuery(null);
    setMentionStart(-1);
  }, []);

  const insertMention = useCallback(
    (agent: SpecialistAgent) => {
      const before = inputValue.slice(0, mentionStart);
      const after = inputValue.slice(
        textareaRef.current?.selectionStart ?? mentionStart + (mentionQuery?.length ?? 0) + 1,
      );
      const newValue = `${before}@${agent.name} ${after}`;
      setInputValue(newValue);
      closeMention();
      // Move cursor after the inserted mention
      const cursorPos = before.length + agent.name.length + 2; // +2 for @ and space
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = cursorPos;
          textareaRef.current.selectionEnd = cursorPos;
          textareaRef.current.focus();
        }
      }, 0);
    },
    [inputValue, mentionStart, mentionQuery, closeMention],
  );

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !isLoading && !disabled && !isDownloadingFiles) {
      onSendMessage({
        message: trimmed,
        imageFiles: pendingImageFiles.length > 0 ? pendingImageFiles : undefined,
        documentFiles: pendingDocumentFiles.length > 0 ? pendingDocumentFiles : undefined,
        sharepointItems: sharepointFiles.length > 0 ? [...sharepointFiles] : undefined,
      });
      setInputValue("");
      setPendingImageFiles([]);
      setPendingDocumentFiles([]);
      setSharepointFiles([]);
      closeMention();
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((f) => ALLOWED_IMAGE_TYPES.has(f.type));
    setPendingImageFiles((prev) => [...prev, ...imageFiles]);
    e.target.value = "";
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Split into images and documents
    const imageFiles = files.filter((f) => ALLOWED_IMAGE_TYPES.has(f.type));
    const docFiles = files.filter((f) => !ALLOWED_IMAGE_TYPES.has(f.type));
    if (imageFiles.length > 0) {
      setPendingImageFiles((prev) => [...prev, ...imageFiles]);
    }
    if (docFiles.length > 0) {
      setPendingDocumentFiles((prev) => [...prev, ...docFiles]);
    }
    e.target.value = "";
  };

  const removeImage = (index: number) => {
    setPendingImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index: number) => {
    setPendingDocumentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeSharePointFile = (index: number) => {
    setSharepointFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const imageFiles = droppedFiles.filter((f) => ALLOWED_IMAGE_TYPES.has(f.type));
    const docFiles = droppedFiles.filter((f) => !ALLOWED_IMAGE_TYPES.has(f.type));
    if (imageFiles.length > 0) {
      setPendingImageFiles((prev) => [...prev, ...imageFiles]);
    }
    if (docFiles.length > 0) {
      setPendingDocumentFiles((prev) => [...prev, ...docFiles]);
    }
  }, []);

  // -----------------------------------------------------------------------
  // SharePoint URL detection & auto-resolve on paste
  // -----------------------------------------------------------------------

  const extractSharePointUrls = useCallback((text: string) => {
    const urls = text.match(SHAREPOINT_URL_RE) || [];
    if (urls.length === 0) return { urls: [], remainingText: text };

    let remaining = text;
    for (const url of urls) {
      remaining = remaining.replace(url, "");
    }
    remaining = remaining.replace(/\n{3,}/g, "\n\n").trim();
    return { urls, remainingText: remaining };
  }, []);

  const resolveAndAttachUrl = useCallback(async (url: string): Promise<boolean> => {
    try {
      const item = await sharepointService.resolveUrl(url);
      if (item.is_folder) return false;
      setSharepointFiles((prev) => {
        if (prev.some((f) => f.id === item.id)) return prev; // dedupe
        return [...prev, item];
      });
      return true;
    } catch (err) {
      console.warn("Could not resolve SharePoint URL:", url, err);
      return false;
    }
  }, []);

  /** Handle paste — intercept pasted images and SharePoint URLs */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Check for pasted image files
      if (e.clipboardData.files.length > 0) {
        const imageFiles = Array.from(e.clipboardData.files).filter((f) => ALLOWED_IMAGE_TYPES.has(f.type));
        if (imageFiles.length > 0) {
          e.preventDefault();
          setPendingImageFiles((prev) => [...prev, ...imageFiles]);
          return;
        }
      }

      const pasted = e.clipboardData.getData("text/plain");
      if (!pasted) return;

      const { urls, remainingText } = extractSharePointUrls(pasted);
      if (urls.length === 0) return; // nothing to intercept

      e.preventDefault();

      // Insert the non-URL remainder into the textarea at the cursor position
      const textarea = e.currentTarget;
      const start = textarea.selectionStart ?? inputValue.length;
      const end = textarea.selectionEnd ?? inputValue.length;
      const newValue = inputValue.slice(0, start) + remainingText + inputValue.slice(end);
      setInputValue(newValue);

      // Resolve each URL in the background
      setIsResolvingUrl(true);
      Promise.all(
        urls.map(async (url) => {
          const ok = await resolveAndAttachUrl(url);
          if (!ok) {
            setInputValue((prev) => (prev ? `${prev} ${url}` : url));
          }
        }),
      ).finally(() => setIsResolvingUrl(false));
    },
    [inputValue, extractSharePointUrls, resolveAndAttachUrl],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setInputValue(value);

    const mention = detectMention(value, cursorPos);
    if (mention) {
      setMentionQuery(mention.query);
      setMentionStart(mention.start);
      return;
    }
    closeMention();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle mention dropdown navigation
    if (mentionQuery !== null && filteredAgents.length > 0) {
      const consumed = handleMentionKeyDown(
        e,
        filteredAgents,
        mentionIndex,
        setMentionIndex,
        insertMention,
        closeMention,
      );
      if (consumed) return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isInputDisabled = isLoading || disabled || isDownloadingFiles;

  return (
    <div
      className={`border-t p-4 bg-surface-primary transition ${
        isDraggingOver ? "border-brand-primary border-dashed" : "border-border-primary"
      }`}
      role="presentation" // NOSONAR — decorative backdrop element
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="max-w-4xl mx-auto">
        {/* Continue Discussion & Synthesize buttons */}
        {(onContinueDiscussion || onSynthesize) && (
          <div className="mb-3 flex justify-center gap-2">
            {onContinueDiscussion && (
              <button
                onClick={onContinueDiscussion}
                disabled={isInputDisabled}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-surface-secondary hover:bg-interactive-active text-content-secondary hover:text-content-primary rounded-lg border border-border-secondary hover:border-border-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MessageSquarePlus className="w-4 h-4" />
                Continue Discussion
              </button>
            )}
            {onSynthesize && (
              <button
                onClick={onSynthesize}
                disabled={isInputDisabled}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-linear-to-r from-mutual-orange/10 to-mutual-purple/10 hover:from-mutual-orange/20 hover:to-mutual-purple/20 text-brand-primary hover:text-orange-300 rounded-lg border border-mutual-orange/30 hover:border-mutual-orange/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-4 h-4" />
                Synthesize
              </button>
            )}
          </div>
        )}

        {/* Drop zone overlay */}
        {isDraggingOver && (
          <div className="flex items-center justify-center gap-2 py-3 mb-3 bg-brand-primary-subtle border border-brand-primary/30 rounded-lg">
            <Paperclip className="w-5 h-5 text-brand-primary" />
            <span className="text-sm text-brand-primary font-medium">Drop files here</span>
          </div>
        )}

        {/* Pending image previews */}
        {pendingImageFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {pendingImageFiles.map((file, index) => (
              <div key={`img-${file.name}-${index}`} className="relative group">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="w-16 h-16 rounded-lg object-cover border border-border-secondary"
                />
                <button
                  onClick={() => removeImage(index)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-surface-tertiary hover:bg-red-500 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pending document file chips */}
        {pendingDocumentFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {pendingDocumentFiles.map((file, index) => (
              <div key={`doc-${file.name}-${index}`} className="relative group">
                <div className="flex items-center gap-2 bg-surface-secondary pl-3 pr-2 py-2 rounded-lg border border-border-secondary">
                  <Paperclip className="w-3.5 h-3.5 text-content-tertiary shrink-0" />
                  <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                </div>
                <button
                  onClick={() => removeDocument(index)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-surface-tertiary hover:bg-red-500 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* SharePoint file chips */}
        {(sharepointFiles.length > 0 || isResolvingUrl) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {sharepointFiles.map((file, index) => (
              <div
                key={file.id}
                className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 pl-3 pr-2 py-2 rounded-lg"
              >
                <Cloud className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                <button
                  onClick={() => removeSharePointFile(index)}
                  className="p-1 hover:bg-interactive-active rounded-sm transition"
                >
                  <X className="w-3 h-3 text-content-tertiary" />
                </button>
              </div>
            ))}
            {isResolvingUrl && (
              <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 pl-3 pr-3 py-2 rounded-lg">
                <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin shrink-0" />
                <span className="text-sm text-content-tertiary">Resolving link...</span>
              </div>
            )}
          </div>
        )}

        {/* Input row — matches Chat layout: action buttons, textarea, send button */}
        <div className="relative">
          {/* @mention autocomplete dropdown */}
          {mentionQuery !== null && filteredAgents.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-72 bg-surface-secondary border border-border-secondary rounded-lg shadow-xl overflow-hidden z-50">
              <div className="px-3 py-1.5 text-xs text-content-quaternary border-b border-border-secondary">
                Mention a persona
              </div>
              {filteredAgents.map((agent, idx) => (
                <button
                  key={agent.uuid}
                  onClick={() => insertMention(agent)}
                  onMouseEnter={() => setMentionIndex(idx)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left transition ${
                    idx === mentionIndex
                      ? "bg-surface-tertiary text-content-primary"
                      : "text-content-secondary hover:bg-surface-tertiary/50"
                  }`}
                >
                  <AgentAvatar agent={agent} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{agent.name}</div>
                    <div className="text-xs text-content-quaternary truncate">{agent.role}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            {/* File attachment button (documents + images) */}
            <label
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition border shrink-0 self-end ${
                isInputDisabled
                  ? "bg-surface-secondary/50 border-border-primary cursor-not-allowed opacity-40"
                  : "bg-surface-secondary hover:bg-interactive-active border-border-secondary cursor-pointer"
              }`}
              title="Attach files"
            >
              <Paperclip className="w-5 h-5 text-content-tertiary" />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={isInputDisabled}
                className="hidden"
              />
            </label>

            {/* Image upload button */}
            <label
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition border shrink-0 self-end ${
                isInputDisabled
                  ? "bg-surface-secondary/50 border-border-primary cursor-not-allowed opacity-40"
                  : "bg-surface-secondary hover:bg-interactive-active border-border-secondary cursor-pointer"
              }`}
              title="Upload images for personas to analyze"
            >
              <ImageIcon className="w-5 h-5 text-content-tertiary" />
              <input
                ref={imageInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                disabled={isInputDisabled}
                className="hidden"
              />
            </label>

            {/* SharePoint / Cloud button */}
            <button
              onClick={() => setIsSharePointBrowserOpen(true)}
              disabled={isInputDisabled}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition border shrink-0 self-end ${
                isInputDisabled
                  ? "bg-surface-secondary/50 border-border-primary cursor-not-allowed opacity-40"
                  : "bg-surface-secondary hover:bg-interactive-active border-border-secondary"
              }`}
              title={getRuntimeConfig().authProvider === "google" ? "Add from Drive" : "Add from SharePoint"}
            >
              <Cloud className="w-5 h-5 text-content-tertiary" />
            </button>

            {/* Voice input button */}
            <div className="shrink-0 self-end">
              <VoiceInputButton
                onTranscript={(text) => setInputValue((prev) => (prev ? `${prev} ${text}` : text))}
                disabled={isInputDisabled}
              />
            </div>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => {
                // Delay close so click on dropdown item registers first
                setTimeout(closeMention, 200);
              }}
              disabled={isInputDisabled}
              placeholder={(() => {
                if (isDownloadingFiles) return "Downloading SharePoint documents...";
                if (isLoading) return "Personas are discussing...";
                if (isActive) return "Discussion in progress...";
                return "Ask a question or @mention a persona (e.g. @Analyst)...";
              })()}
              className="flex-1 bg-surface-secondary text-content-primary px-4 py-3 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary placeholder-content-quaternary resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              rows={3}
            />

            {/* Send / Stop button */}
            <div className="shrink-0 self-end">
              {isLoading && onInterrupt ? (
                <button
                  onClick={onInterrupt}
                  className="flex items-center justify-center w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Stop discussion"
                  disabled={disabled}
                >
                  <StopCircle className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={
                    (!inputValue.trim() &&
                      pendingImageFiles.length === 0 &&
                      pendingDocumentFiles.length === 0 &&
                      sharepointFiles.length === 0) ||
                    isInputDisabled
                  }
                  className="flex items-center justify-center w-10 h-10 bg-brand-gradient hover:opacity-90 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Send message"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 text-white" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 text-xs text-content-quaternary">
          Press Enter to send, Shift+Enter for new line, @ to mention a persona
          {isLoading && " \u2022 Personas are collaborating on your query"}
        </div>
      </div>

      {/* Cloud Storage Browser Modal */}
      {isSharePointBrowserOpen && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
            {getRuntimeConfig().authProvider === "google" ? (
              <GoogleDriveBrowser
                onSelect={(selection) => {
                  setSharepointFiles((prev) => {
                    const existingIds = new Set(prev.map((f) => f.id));
                    const newFiles = selection.items
                      .filter((f) => !existingIds.has(f.id))
                      .map((f) => ({ id: f.id, name: f.name, web_url: f.web_url, is_folder: f.is_folder }));
                    return [...prev, ...newFiles];
                  });
                  setIsSharePointBrowserOpen(false);
                }}
                onCancel={() => setIsSharePointBrowserOpen(false)}
                multiSelect
                filesOnly
                title="Add Drive Files"
              />
            ) : (
              <SharePointBrowser
                onSelect={(selection) => {
                  setSharepointFiles((prev) => {
                    const existingIds = new Set(prev.map((f) => f.id));
                    const newFiles = selection.items.filter((f) => !existingIds.has(f.id));
                    return [...prev, ...newFiles];
                  });
                  setIsSharePointBrowserOpen(false);
                }}
                onCancel={() => setIsSharePointBrowserOpen(false)}
                multiSelect
                filesOnly
                allowOneDrive
                title="Add SharePoint Files"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
